import CustomCodeCard from '@/components/CustomCodes/CustomCodeCard';
import CustomCodeEditorModal from '@/components/CustomCodes/CustomCodeEditorModal';
import SettingSectionTitle from '@/components/settings/SettingSectionTitle';
import { SettingsTextField } from '@/components/settings/SettingsTextField.tsx';
import { AuthContext } from '@/contexts/AuthContext.tsx';
import { useDashboardContext } from '@/contexts/DashboardContext';
import { UserContext } from '@/contexts/UserContext';
import { PlusIcon } from '@heroicons/react/24/outline';
import {
  CustomCodeReference,
  CustomCode as CustomCodeType,
} from '@superfunnel/types';
import AlertModal from '@superfunnel/ui-core/AlertModal';
import Button from '@superfunnel/ui-core/v2/Button';
import CodeInput from '@superfunnel/ui-core/v2/CodeInput';
import {
  getCustomCodeSource,
  getEffectiveCustomCodes,
  isCustomCodeOverridden,
  toggleCustomCodeReference,
  updateCustomCode,
} from '@superfunnel/utils';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useDynamicPageSettingsContext } from './DynamicPageSettingsContext';
import { DynamicPageSettingsModalTabWrapper } from './DynamicPageSettingsModalTabWrapper';

export const CustomCode = () => {
  const { isSupportAdmin } = useContext(AuthContext);
  const { selectedAccount } = useContext(UserContext);
  const { dynamicPage, updatePage } = useDynamicPageSettingsContext();
  const { accountConfig } = useDashboardContext();
  const [edit, setEdit] = useState<boolean>(false);

  // Legacy custom code fields
  const [headCustomCode, setHeadCustomCode] = useState<string>('');
  const [headCustomCodeDelay, setHeadCustomCodeDelay] = useState<number>(0);
  const [bodyCustomCode, setBodyCustomCode] = useState<string>('');
  const [bodyCustomCodeDelay, setBodyCustomCodeDelay] = useState<number>(0);

  // Check if we have any legacy data to show
  const hasLegacyData = useMemo(() => {
    return !!(
      dynamicPage?.config?.headCustomCode ||
      dynamicPage?.config?.bodyCustomCode ||
      dynamicPage?.config?.headCustomCodeDelay ||
      dynamicPage?.config?.bodyCustomCodeDelay
    );
  }, [dynamicPage?.config]);

  // Custom codes
  const [customCodes, setCustomCodes] = useState<CustomCodeType[]>([]);
  const [customCodesReferences, setCustomCodesReferences] = useState<
    CustomCodeReference[]
  >([]);

  // Editor modal state
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingCode, setEditingCode] = useState<CustomCodeType | undefined>();
  const [deleteConfirm, setDeleteConfirm] = useState<string | undefined>();

  const accountCodes = useMemo(
    () => accountConfig?.customCodes || [],
    [accountConfig?.customCodes],
  );

  const effectiveCodes = useMemo(
    () =>
      getEffectiveCustomCodes(
        accountCodes,
        customCodes,
        undefined,
        customCodesReferences,
        undefined,
      ),
    [accountCodes, customCodes, customCodesReferences],
  );

  useEffect(() => {
    if (!dynamicPage) return;

    setHeadCustomCode(dynamicPage?.config?.headCustomCode || '');
    setHeadCustomCodeDelay(dynamicPage?.config?.headCustomCodeDelay || 0);
    setBodyCustomCode(dynamicPage?.config?.bodyCustomCode || '');
    setBodyCustomCodeDelay(dynamicPage?.config?.bodyCustomCodeDelay || 0);
    setCustomCodes(dynamicPage?.config?.customCodes || []);
    setCustomCodesReferences(dynamicPage?.config?.customCodesReferences || []);
  }, [dynamicPage]);

  // Helper to compare arrays
  const arraysEqual = (a: any[], b: any[]) => {
    if (a.length !== b.length) return false;
    return JSON.stringify(a) === JSON.stringify(b);
  };

  useEffect(() => {
    if (!dynamicPage) return;
    const savedCustomCodes = dynamicPage?.config?.customCodes || [];
    const savedCustomCodesReferences =
      dynamicPage?.config?.customCodesReferences || [];

    // Check for legacy field changes
    const legacyChanged =
      hasLegacyData &&
      ((dynamicPage?.config?.headCustomCode ?? '') !== headCustomCode ||
        (dynamicPage?.config?.headCustomCodeDelay ?? 0) !==
          headCustomCodeDelay ||
        (dynamicPage?.config?.bodyCustomCode ?? '') !== bodyCustomCode ||
        (dynamicPage?.config?.bodyCustomCodeDelay ?? 0) !==
          bodyCustomCodeDelay);

    setEdit(
      legacyChanged ||
        !arraysEqual(savedCustomCodes, customCodes) ||
        !arraysEqual(savedCustomCodesReferences, customCodesReferences),
    );
  }, [
    dynamicPage,
    customCodes,
    customCodesReferences,
    hasLegacyData,
    headCustomCode,
    headCustomCodeDelay,
    bodyCustomCode,
    bodyCustomCodeDelay,
  ]);

  const onDiscard = () => {
    setEdit(false);
    setHeadCustomCode(dynamicPage?.config?.headCustomCode || '');
    setHeadCustomCodeDelay(dynamicPage?.config?.headCustomCodeDelay || 0);
    setBodyCustomCode(dynamicPage?.config?.bodyCustomCode || '');
    setBodyCustomCodeDelay(dynamicPage?.config?.bodyCustomCodeDelay || 0);
    setCustomCodes(dynamicPage?.config?.customCodes || []);
    setCustomCodesReferences(dynamicPage?.config?.customCodesReferences || []);
  };

  const onSave = () => {
    if (!dynamicPage) return;

    setEdit(false);
    updatePage({
      config: {
        ...dynamicPage.config,
        customCodes,
        customCodesReferences,
        ...(hasLegacyData && {
          headCustomCode,
          headCustomCodeDelay,
          bodyCustomCode,
          bodyCustomCodeDelay,
        }),
      },
    });
  };

  // Advanced custom codes handlers
  const handleAddCode = () => {
    setEditingCode(undefined);
    setEditorOpen(true);
  };

  const handleEditCode = (code: CustomCodeType) => {
    setEditingCode(code);
    setEditorOpen(true);
  };

  const handleSaveCode = useCallback(
    (code: CustomCodeType) => {
      const isNew = !customCodes.find((c) => c.id === code.id);

      if (isNew) {
        setCustomCodes([...customCodes, code]);
      } else {
        const updatedCodes = customCodes.map((c) =>
          c.id === code.id ? code : c,
        );
        setCustomCodes(updatedCodes);
      }
    },
    [customCodes],
  );

  const handleToggleCode = useCallback(
    (codeId: string, enabled: boolean) => {
      const source = getCustomCodeSource(codeId, accountCodes, customCodes);

      if (source === 'dynamicPage') {
        // Toggle dynamicPage code directly
        const updatedCodes = customCodes.map((c) =>
          c.id === codeId ? updateCustomCode(c, { enabled }) : c,
        );
        setCustomCodes(updatedCodes);
      } else if (source === 'account') {
        // Toggle account code via reference
        const updatedRefs = toggleCustomCodeReference(
          codeId,
          customCodesReferences,
          enabled,
        );
        setCustomCodesReferences(updatedRefs);
      }
    },
    [accountCodes, customCodes, customCodesReferences],
  );

  const handleDeleteCode = useCallback(
    (codeId: string) => {
      const updatedCodes = customCodes.filter((c) => c.id !== codeId);
      setCustomCodes(updatedCodes);
      setDeleteConfirm(undefined);
    },
    [customCodes],
  );

  // Group codes by source for better organization
  const groupedCodes = useMemo(
    () => ({
      account: effectiveCodes.filter((code) =>
        accountCodes.some((c) => c.id === code.id),
      ),
      dynamicPage: effectiveCodes.filter((code) =>
        customCodes.some((c) => c.id === code.id),
      ),
    }),
    [effectiveCodes, accountCodes, customCodes],
  );
  return (
    <DynamicPageSettingsModalTabWrapper
      edit={edit}
      onDiscard={onDiscard}
      onSave={onSave}
    >
      <div className="col-span-2 grid gap-4">
        <SettingSectionTitle
          title="Custom Codes"
          description="Manage custom code snippets for this dynamic page. Account-level codes are inherited and can be toggled on/off."
        />

        {hasLegacyData && (
          <>
            <SettingSectionTitle
              title="Legacy Custom Code"
              description="These legacy fields are shown because they contain existing data. New custom codes should be added below."
            />
            <CodeInput
              title={'Head'}
              titleClassName={
                'text-base-700 flex items-center gap-2 text-sm font-medium'
              }
              value={headCustomCode}
              onChange={(v) => setHeadCustomCode(v)}
              language={'javascript'}
              inlineLines={5}
              placeholder={'<!-- Add your custom code here -->'}
            />

            {isSupportAdmin && (
              <SettingsTextField
                title="Head Delay"
                value={headCustomCodeDelay + ''}
                setValue={(value: any) => {
                  setHeadCustomCodeDelay(Number.isNaN(+value) ? 0 : +value);
                }}
                pattern="^\d*$"
                prefix="milliseconds"
              />
            )}
            <CodeInput
              title={'Body'}
              titleClassName={
                'text-base-700 flex items-center gap-2 text-sm font-medium'
              }
              value={bodyCustomCode}
              onChange={(v) => setBodyCustomCode(v)}
              language={'javascript'}
              inlineLines={5}
              placeholder={'<!-- Add your custom code here -->'}
            />

            {isSupportAdmin && (
              <SettingsTextField
                title="Body Delay"
                value={bodyCustomCodeDelay + ''}
                setValue={(value: any) => {
                  setBodyCustomCodeDelay(Number.isNaN(+value) ? 0 : +value);
                }}
                pattern="^\d*$"
                prefix="milliseconds"
              />
            )}

            <div className="border-base-200 my-4 border-t" />
          </>
        )}

        <div className="mt-4 border-t border-gray-200 pt-4">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-sm font-medium text-gray-900">
              Dynamic Page Custom Codes
            </h3>
            <Button size="sm" onClick={handleAddCode}>
              <PlusIcon className="-ml-0.5 h-4 w-4" />
              Add Code
            </Button>
          </div>

          {groupedCodes.dynamicPage.length === 0 &&
          groupedCodes.account.length === 0 ? (
            <div className="rounded-lg border-2 border-dashed border-gray-300 p-12 text-center">
              <p className="text-sm text-gray-500">
                No custom codes yet. Add your first custom code to get started.
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Dynamic Page-specific codes */}
              {groupedCodes.dynamicPage.length > 0 && (
                <div className="space-y-3">
                  {groupedCodes.dynamicPage.map((code) => {
                    // Dynamic Page codes are never overridden (they're the source)
                    const isOverridden = false;
                    return (
                      <CustomCodeCard
                        key={code.id}
                        customCode={code}
                        source="dynamicPage"
                        isOverridden={isOverridden}
                        displayEnabled={code.enabled}
                        onToggle={handleToggleCode}
                        onEdit={handleEditCode}
                        onDelete={(id) => setDeleteConfirm(id)}
                      />
                    );
                  })}
                </div>
              )}

              {/* Inherited account codes */}
              {groupedCodes.account.length > 0 && (
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-gray-700">
                    Inherited from Account
                  </h4>
                  {groupedCodes.account.map((code) => {
                    // Find the original account code to get its original enabled state
                    const originalCode = accountCodes.find(
                      (c) => c.id === code.id,
                    );
                    const isOverridden = isCustomCodeOverridden(
                      code.id,
                      originalCode?.enabled ?? true,
                      customCodesReferences,
                    );
                    return (
                      <CustomCodeCard
                        key={code.id}
                        customCode={code}
                        source="account"
                        isInherited
                        isOverridden={isOverridden}
                        displayEnabled={code.enabled}
                        onToggle={handleToggleCode}
                        readOnly
                      />
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        <CustomCodeEditorModal
          customCode={editingCode}
          open={editorOpen}
          onClose={() => {
            setEditorOpen(false);
            setEditingCode(undefined);
          }}
          onSave={handleSaveCode}
          userId={selectedAccount?.data().createdBy || 'unknown'}
        />

        <AlertModal
          title="Delete Custom Code"
          open={!!deleteConfirm}
          onClose={() => setDeleteConfirm(undefined)}
          actions={[
            {
              type: 'secondary',
              title: 'Cancel',
              onClick: () => setDeleteConfirm(undefined),
            },
            {
              type: 'primary',
              color: 'negative',
              title: 'Delete',
              onClick: () => deleteConfirm && handleDeleteCode(deleteConfirm),
            },
          ]}
          type="warn"
        >
          <p>
            Are you sure you want to delete this custom code? This action cannot
            be undone.
          </p>
        </AlertModal>
      </div>
    </DynamicPageSettingsModalTabWrapper>
  );
};
